import { assertNever } from "../../../util/assertNever";
import { ArrayColumnOperators, CommonColumnOperators } from "../enum.filterOperators";
import { ArrayFilterType, GenericTableFilterType } from "../type.genericTableQuery";
import type { FilterMapper } from "./interface.filterMapper";

export class ArrayFilterMapper implements FilterMapper {
  toQuery({ filter }: ArrayFilterType): undefined | string {
    const option = filter.option;
    switch (option) {
      case ArrayColumnOperators.CONTAINS_ANY: {
        const arrayValues = filter.value;
        // Join array values with comma
        return `${ArrayColumnOperators.CONTAINS_ANY}$-$${arrayValues?.join(",") ?? ""}`;
      }
      case CommonColumnOperators.VALUE_EXISTS: {
        const missingValue = filter.value;
        return `${CommonColumnOperators.VALUE_EXISTS}$-$${missingValue}`;
      }
      default:
        assertNever(option);
    }
  }

  fromQuery(value: string): ArrayFilterType {
    const [columnOperator, filterValue] = value.split("$-$");
    if (columnOperator === undefined || filterValue === undefined) {
      throw new Error("missing filter value or option");
    }
    const option = this.parseArrayColumnOperator(columnOperator);

    switch (option) {
      case CommonColumnOperators.VALUE_EXISTS:
        return {
          type: GenericTableFilterType.ARRAY,
          filter: { option, value: filterValue === "true" },
        };
      case ArrayColumnOperators.CONTAINS_ANY:
        // Split comma-separated values into array
        return {
          type: GenericTableFilterType.ARRAY,
          filter: { option, value: filterValue ? filterValue.split(",").filter((value) => value.length > 0) : [] },
        };
      default:
        assertNever(option);
    }
  }

  private parseArrayColumnOperator(option: string): ArrayColumnOperators | CommonColumnOperators.VALUE_EXISTS {
    const allOptions = [...Object.values(ArrayColumnOperators), CommonColumnOperators.VALUE_EXISTS];
    if (allOptions.includes(option as ArrayColumnOperators | CommonColumnOperators.VALUE_EXISTS)) {
      return option as ArrayColumnOperators | CommonColumnOperators.VALUE_EXISTS;
    }
    throw new Error(`Invalid array filter option: ${option}`);
  }
}
